import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form } from 'react-bootstrap';
import programService from '@/shared/services/programService';
import LAContractAcknowledgment from './LAContractAcknowledgment';

// Loads what the LA program asks of faculty and of the LA (#404). Shared by
// the create wizard and the edit/resubmit flow. Waits until `enabled` (LA
// selected) and the term is known, since the training dates are per term.
export function useLARequirements(termCode, enabled = true) {
    // { termCode, data } or { termCode, error: true }. Remembering the term
    // means a new term never shows the old term's data, not even for a render.
    const [result, setResult] = useState(null);
    // Bumped by retry() to refetch without a page refresh
    const [attempt, setAttempt] = useState(0);
    const active = enabled && !!termCode;

    useEffect(() => {
        if (!active) return undefined;
        let cancelled = false;
        programService
            .getProgramRequirements('la', termCode)
            .then((data) => {
                if (!cancelled) setResult({ termCode, data });
            })
            .catch(() => {
                if (!cancelled) setResult({ termCode, error: true });
            });
        return () => {
            cancelled = true;
        };
    }, [termCode, active, attempt]);

    const retry = useCallback(() => {
        setResult(null);
        setAttempt((n) => n + 1);
    }, []);

    const current = active && result?.termCode === termCode ? result : null;
    return {
        requirements: current?.data ?? null,
        loading: active && !current,
        loadError: !!current?.error,
        // No term to load for (none open), so there's nothing to wait on
        noTerm: enabled && !termCode,
        retry,
    };
}

// What the LA themself must do (training), from the LA program's
// config (#404). Separate from LAContractAcknowledgment, which is the
// faculty member's own obligations. Both groups are shown because faculty
// often don't know yet whether their LA will be new or returning.
const GROUPS = [
    { key: 'all', label: 'All LAs' },
    { key: 'new', label: 'New LAs' },
    { key: 'returning', label: 'Returning LAs' },
];

function LAStudentRequirements({
    acknowledgesLARequirements,
    onChange,
    idPrefix,
    requirements = [],
    trainingSessions = [],
    // true until the LA requirements load; LACommitmentCards shows why
    unavailable = false,
    isInvalid = false,
}) {
    const checkboxId = `${idPrefix}-acknowledges-la-requirements`;
    const errorId = `${checkboxId}-error`;

    return (
        <>
            {!unavailable &&
                GROUPS.map(({ key, label }) => {
                    const items = requirements.filter(
                        (r) => r.appliesTo === key
                    );
                    const sessions = trainingSessions.filter(
                        (s) => s.appliesTo === key
                    );
                    if (!items.length && !sessions.length) return null;
                    return (
                        <section key={key} className="mb-3">
                            <h4 className="h6 fw-semibold">{label}</h4>
                            <ul className="mb-0">
                                {items.map((r, i) => (
                                    <li key={`${i}-${r.text}`}>
                                        {r.text}
                                        {r.consequence && (
                                            <Alert
                                                variant="warning"
                                                className="py-2 px-3 my-2 small"
                                            >
                                                {r.consequence}
                                            </Alert>
                                        )}
                                    </li>
                                ))}
                                {sessions.map((s, i) => (
                                    <li key={`${i}-${s.title}`}>
                                        <span className="fw-semibold">
                                            {s.title}:
                                        </span>{' '}
                                        {s.when}
                                    </li>
                                ))}
                            </ul>
                        </section>
                    );
                })}
            <Form.Check
                type="checkbox"
                id={checkboxId}
                label={
                    <>
                        I understand my LA must be available for these
                        requirements. <span className="text-danger">*</span>
                    </>
                }
                checked={!unavailable && acknowledgesLARequirements === true}
                onChange={(e) => onChange(e.target.checked)}
                disabled={unavailable}
                isInvalid={isInvalid}
                aria-invalid={isInvalid}
                aria-describedby={isInvalid ? errorId : undefined}
            />
            {isInvalid && (
                <Form.Control.Feedback
                    type="invalid"
                    id={errorId}
                    role="alert"
                    className="d-block"
                >
                    Acknowledgment is required.
                </Form.Control.Feedback>
            )}
        </>
    );
}

// Single source of truth for "is Acknowledgments & Contract complete" —
// CreateApplication.js uses this as its own step; ApplicationDetailPage.js's
// edit/resubmit flow folds the same checks into its own Program
// Questionnaire step instead (see getProgramSpecificErrors there), which is
// why this takes an idPrefix like getGeneralAcknowledgmentErrors/
// getReviewConfirmationErrors/getProgramSelectionErrors do — both callers
// build the ids from whatever components they're rendering. `loaded` is
// whether the LA requirements are on screen: a box saved as ticked doesn't
// count until the faculty member can actually see what it covers, and until
// then the error points at the loading/retry message, not the disabled boxes.
export function getAcknowledgementContractErrors(
    la,
    idPrefix = 'ca',
    loaded = true,
    noTerm = false
) {
    if (!loaded) {
        return [
            {
                id: `${idPrefix}-la-requirements-status`,
                label: noTerm
                    ? 'LA contract (no open term)'
                    : 'LA contract (not loaded yet)',
            },
        ];
    }
    const errors = [];
    if (la?.acknowledgesContract !== true) {
        errors.push({
            id: `${idPrefix}-acknowledges-la-contract`,
            label: 'LA contract acknowledgment',
        });
    }
    if (la?.acknowledgesLARequirements !== true) {
        errors.push({
            id: `${idPrefix}-acknowledges-la-requirements`,
            label: 'LA requirements acknowledgment',
        });
    }
    return errors;
}

// Both LA cards, shared by this step and ApplicationDetailPage's edit flow.
// `requirements` is useLARequirements' result. Loading and load errors are
// shown once here, for both cards.
export function LACommitmentCards({
    la,
    onChange,
    idPrefix,
    requirements = {},
    errorIds = new Set(),
}) {
    const data = requirements.requirements;
    const unavailable = !data;
    return (
        <>
            {unavailable && (
                <div
                    id={`${idPrefix}-la-requirements-status`}
                    tabIndex={-1}
                    className="mb-3"
                >
                    {requirements.noTerm ? (
                        <Alert variant="warning" className="mb-0">
                            There&apos;s no open term to apply for right now, so
                            the LA contract can&apos;t be shown.
                        </Alert>
                    ) : requirements.loadError ? (
                        <Alert variant="danger" className="mb-0">
                            We couldn&apos;t load the LA contract.{' '}
                            {requirements.retry && (
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={requirements.retry}
                                >
                                    Try again
                                </Button>
                            )}
                        </Alert>
                    ) : (
                        <p className="text-muted mb-0" role="status">
                            Loading the LA contract…
                        </p>
                    )}
                </div>
            )}
            <Card className="mb-3">
                <Card.Header as="h3" className="h6 mb-0">
                    What you&apos;re committing to as a mentor
                </Card.Header>
                <Card.Body>
                    <LAContractAcknowledgment
                        acknowledgesContract={la.acknowledgesContract}
                        onChange={(checked) =>
                            onChange('acknowledgesContract', checked)
                        }
                        idPrefix={idPrefix}
                        commitments={data?.mentorCommitments}
                        unavailable={unavailable}
                        isInvalid={errorIds.has(
                            `${idPrefix}-acknowledges-la-contract`
                        )}
                    />
                </Card.Body>
            </Card>
            <Card className="mb-3">
                <Card.Header as="h3" className="h6 mb-0">
                    What your LA will need to do
                </Card.Header>
                <Card.Body>
                    <LAStudentRequirements
                        acknowledgesLARequirements={
                            la.acknowledgesLARequirements
                        }
                        onChange={(checked) =>
                            onChange('acknowledgesLARequirements', checked)
                        }
                        idPrefix={idPrefix}
                        requirements={data?.studentRequirements}
                        trainingSessions={data?.trainingSessions}
                        unavailable={unavailable}
                        isInvalid={errorIds.has(
                            `${idPrefix}-acknowledges-la-requirements`
                        )}
                    />
                </Card.Body>
            </Card>
        </>
    );
}

// Section 3: Acknowledgments & Contract
function CreateApplicationSection3({
    la,
    onChange,
    requirements,
    errorIds = new Set(),
}) {
    return (
        <div>
            <h2 className="h5 mb-3">Acknowledgments &amp; Contract</h2>
            <LACommitmentCards
                la={la}
                onChange={onChange}
                idPrefix="ca"
                requirements={requirements}
                errorIds={errorIds}
            />
        </div>
    );
}

export default CreateApplicationSection3;
